import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import type { UseFormReturn } from "react-hook-form";
import { EventSchema,  UserManagementDPS } from "@/components/event/EventSchema";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Dropdown } from "@/components/ui/filterSelect";
import { Popup } from "@/components/common/Popup";
import { UserInfo } from "@/components/user/UserInfo";

const REGISTRATION_TYPES = [
  { Id: 1, Name: "Standard" },
  { Id: 2, Name: "SHS Analyst Call" },
];
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Toggle } from "@/components/ui/toggle";
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { UserInfoSchema } from "../user/UserSchema";
import { PresenterInfo } from "../presenter/PresenterInfo";
import { SearchForm } from "../common/SearchForm";

type Lookup = { Id: number; Value: string; Name?: string };


type Props = {
  form: UseFormReturn<z.infer<typeof EventSchema>>;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  eventId: string | null;
  onError: (message: string) => void;

};




export function UserManagement({ form, apiFetch, eventId, onError }: Props) {
  const [userManagementTypes, setUserManagementTypes] = useState<Lookup[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [editPresenterId, setPresenter] = useState<string|null>(null);
  const [isGeneratingPasswordField, setIsGeneratingPasswordField] = useState<"PWModerator" | "PW" | null>(null);
const [dps, setdps] = useState<UserManagementDPS[]>([]);
const [users, setUsers] = useState<UserInfoSchema[]>([]);
const [showImport, setShowImport] = useState(false);
const [autoGeneratePW, setAutoGeneratePW] = useState(false);
const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [searchText, setSearchText] = useState<string>("");

const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserInfoSchema | null>(null);
  const selectedUserManagementTypeId = Number(form.watch("UserManagementTypeId") ?? 0);
  const hideExplicitLogin = form.watch("UserManagementHideExplicitLogin");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadUsers = async () => {
    const usersRes = await apiFetch(`/api/EventPool/GetUsersByEvent?eventId=${eventId ?? ""}`);
    if (!usersRes.ok) {
      onError("Failed to fetch users");
    }

    const usersJson = await usersRes.json();
    setUsers(usersJson);
  };

  const handleSearchSubmit = (value: string) => {
    setSearchText(value);
  };


  function applySearchFilter(data: UserInfoSchema[], query: string): UserInfoSchema[] {
    const term = query.trim().toLowerCase();
    if (term == '') return data;
    if (!data) return data;
    return data.filter(item =>
      item.UserName.toLowerCase().includes(term) ||
      (item.LastName ?? "").toLowerCase().includes(term) ||
      (item.FirstName ?? "").toLowerCase().includes(term)
    );
  }

  const filteredData = applySearchFilter(users, searchText);
  const generateSystemUsers = async () => {
    const usersRes = await apiFetch(`/api/EventPool/RegenerateEventUsers?eventId=${eventId ?? ""}`);
    if (!usersRes.ok) {
      onError("Failed to create users");
    }

    loadUsers();
  }

  
  const deleteUser = async (id :string) => {
    const usersRes = await apiFetch(`/api/EventPool/DeleteUser?key=${id}&eventId=${eventId ?? ""}`, {
      method: "DELETE",
    });
    if (!usersRes.ok) {
      onError("Failed to delete user");
    }

    setUsers((prev) => prev.filter((u) => u.Id !== id));
  }

const downloadSample = async () => {
      let res = await apiFetch(`/api/EventPool/GetImportSample`, { method: "GET" });
      //download res as file
      if (!res.ok) {
        const eText = await res.text();
        onError(eText || "Failed to download sample file");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href  = url;  
      a.download = "user_import_sample.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);


}

  const createUtf16CsvBlob = (text: string) => {
    const buffer = new ArrayBuffer(2 + text.length * 2);
    const view = new DataView(buffer);

    // UTF-16LE BOM helps Excel detect the encoding correctly.
    view.setUint16(0, 0xfeff, true);

    for (let index = 0; index < text.length; index += 1) {
      view.setUint16(2 + index * 2, text.charCodeAt(index), true);
    }

    return new Blob([buffer], { type: "text/csv;charset=utf-16le;" });
  };

  const triggerDownload = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setSelectedImportFile(selected);

  }
  const importUsers = async () => {
    const file = selectedImportFile ?? fileInputRef.current?.files?.[0] ?? null;
    if (!file) return;

    setIsUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("myFile", file);
      fd.append("eventId", eventId ?? "");
      fd.append("initPassword", autoGeneratePW ? "true" : "false");

      let res = await apiFetch(`/api/EventPool/UploadUserImportFile`, { method: "POST", body: fd });


      if (!res.ok) {
        const eText = await res.text();
        onError(eText || "Failed to upload user import file");
    
        return;
      }

      const importReport = await res.text();
      if (importReport.trim() !== "") {
        triggerDownload(createUtf16CsvBlob(importReport), "user-import-report.csv");
      }

      await loadUsers();
      setSelectedImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error(err);
      
    } finally {
      setIsUploadingFile(false);
    }
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [typesRes, domainsRes, idpsRes, usersRes] = await Promise.all([
          apiFetch(`/api/EventPool/GetUserManagemetTypes`),
          apiFetch(`/api/EventPool/GetAllDomains`),
          apiFetch(`/api/EventPool/GetUserManagementIdPs?eventId=${eventId ?? ""}`),
          apiFetch(`/api/EventPool/GetUsersByEvent?eventId=${eventId ?? ""}`),
        ]);

        if (!active) return;

        if (typesRes.ok) {
          const typesJson = await typesRes.json();
          if (active) setUserManagementTypes(typesJson.data);
        }

        if (domainsRes.ok) {
          const domainsJson = await domainsRes.json();
          if (active) setDomains(domainsJson.data);
        }

        if (idpsRes.ok) {
          const idpsJson = await idpsRes.json();
          if (active) setdps(idpsJson.data);
        }

          if (usersRes.ok) {
          const usersJson = await usersRes.json();
          if (active) setUsers(usersJson);
        }
      } catch (err) {
        console.error(err);
        onError("Failed to load user management settings");
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [apiFetch, eventId, onError]);

  const updateIdpRow = (index: number, patch: Partial<UserManagementDPS>) => {
    setdps((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleGeneratePassword = async (fieldName: "PWModerator" | "PW") => {
    try {
      setIsGeneratingPasswordField(fieldName);

   
     
        const res = await apiFetch('/api/eventpool/GenerateRandomPassword');
       
      

      if (!res.ok) {
        onError("Failed to generate password");
        return;
      }

      const rawText = await res.text();
      const generatedPassword = rawText.startsWith('"') ? JSON.parse(rawText) : rawText.trim();
      if (!generatedPassword) {
        onError("Password generation returned no value");
        return;
      }

      form.setValue(fieldName, generatedPassword, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    } catch (err) {
      console.error(err);
      onError("Failed to generate password");
    } finally {
      setIsGeneratingPasswordField(null);
    }
  };



  return (
    <div className="mt-2 space-y-4">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-start">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <FormField
              control={form.control}
              name="UserManagementTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Management Type *</FormLabel>
                  <FormControl>
                    <Dropdown
                      source={userManagementTypes}
                      value={field.value}
                      onChange={(value) => {
                        const nextValue = Number(value);
                        field.onChange(nextValue);
                        if (nextValue === 2) {
                          form.setValue("UserManagementHideExplicitLogin", true, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                      getLabel={(x) => x.Name ?? ""}
                      getValue={(x) => x.Id}
                      searchable={true}
                      disabled={false}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {selectedUserManagementTypeId == 1 && (
              <FormField
                control={form.control}
                name="PW"
                render={({ field }) => (
                  <FormItem className="max-w-[180px]">
                    <FormLabel>Shared Password</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <Input {...field} className="w-[136px]" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleGeneratePassword("PW")}
                          disabled={isGeneratingPasswordField === "PW"}
                          title="Generate password"
                        >
                          {isGeneratingPasswordField === "PW" ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          ) : (
                            <RefreshCw className="h-4 w-4" aria-hidden="true" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {(selectedUserManagementTypeId === 1 || selectedUserManagementTypeId === 0) && (
              <FormField
                control={form.control}
                name="UserManagementHideUserFields"
                render={({ field }) => (
                  <FormItem className="w-[10rem]">
                    <FormControl>
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={(checked) => field.onChange(checked)}
                        label="Hide Username & Company fields"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {selectedUserManagementTypeId !== 2 && (
              <FormField
                control={form.control}
                name="UserManagementHideExplicitLogin"
                render={({ field }) => (
                  <FormItem className="relative ">
                    <FormLabel>Explicit Login</FormLabel>

                    <FormControl className="h-[2.3rem]">
                      <Toggle
                        checked={!field.value}
                        onChange={(e) => field.onChange(!e.target.checked)}
                        name={field.name}
                        onBlur={field.onBlur}
                        ref={field.ref}
                      />
                    </FormControl>

                    <span className="group absolute right-0 top-[1.5rem] cursor-default">
                      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-slate-400 text-[9px] font-bold text-white leading-none">?</span>
                      <span className="pointer-events-none absolute bottom-full right-0 z-[9999] mb-1 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white opacity-0 shadow group-hover:opacity-100">
                        A seperate login option will appear in the toolbar for presenters to log in
                      </span>
                    </span>
                  </FormItem>
                )}
              />
            )}

          </div>
          {selectedUserManagementTypeId == 3 && (
            <div>{`Template Path: https://one.beamstream.eu/${form.getValues("EventPath")}/EntryPoint?email=doe.jane@test.com&firstName=Jane&lastName=Doe`}</div>
          )}
 <FormField
              control={form.control}
              name="PWModerator"
              render={({ field }) => (
                <FormItem className="max-w-[180px]">
                  <FormLabel>PWModerator</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input {...field} className="w-[136px]" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleGeneratePassword("PWModerator")}
                        disabled={isGeneratingPasswordField === "PWModerator"}
                        title="Generate password"
                      >
                        {isGeneratingPasswordField === "PWModerator" ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                          <RefreshCw className="h-4 w-4" aria-hidden="true" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          {(selectedUserManagementTypeId === 2) && (
            <FormField
              control={form.control}
              name="AllowIncognito"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Checkbox
                      checked={!!field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                      label="Allow Incognito"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="Domains"
            render={({ field }) => (
              <FormItem className="max-w-2xl">
                <FormLabel>Domains for internal users, others will be shown with (EXT) suffix</FormLabel>
                <FormControl>
                  <>
                    <Input {...field} list="domain-suggestions" placeholder="Select..." />
                    <datalist>
                      {domains.map((domain) => (
                        <option key={domain} value={domain} />
                      ))}
                    </datalist>
                  </>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="shrink-0">
          {(selectedUserManagementTypeId === 2 || hideExplicitLogin === false) && (
            <div className="inline-block rounded-md border">
              {selectedUserManagementTypeId !== 2 && (
                <div className="bg-slate-100 px-3 py-1 text-sm font-bold text-gray-600">Rules for explicit Login:</div>
              )}
              <table className="text-sm">
                <thead className="bg-slate-100 text-left">
                  <tr>
                    <th className="w-[60px] px-3 py-2 text-center">Selected</th>
                    <th className="px-3 py-2">Display Text</th>
                    <th className="w-[60px] px-3 py-2 text-center">
                      <span className="relative inline-flex items-center gap-1">
                        Strict
                        <span className="group relative cursor-default">
                          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-slate-400 text-[9px] font-bold text-white leading-none">?</span>
                          <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white opacity-0 shadow group-hover:opacity-100">
                            Strict means, only users in the list below can log in
                          </span>
                        </span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dps.map((item, index) => {
                    const isLocalRow = index === 0;
                    const showRegType = isLocalRow && item.Selected && !item.IsStrict;
                    return (
                      <tr key={item.UMPTId} className="border-t">
                        <td className="w-[60px] px-3 py-2 text-center">
                          <Toggle
                            checked={!!item.Selected}
                            onChange={(e) => updateIdpRow(index, { Selected: e.target.checked })}
                          />
                        </td>
                        <td className="px-3 py-2">{item.DisplayName}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              checked={!!item.IsStrict}
                              onCheckedChange={(checked) => {
                                const isStrict = !!checked;
                                updateIdpRow(index, {
                                  IsStrict: isStrict,
                                  RegistrationTypeID: isStrict ? 0 : 1,
                                });
                              }}
                            />
                            {showRegType && (
                              <div className="flex w-[11rem] flex-1 items-center gap-2">
                                or
                                <div className="w-full">
                                  Allow Registration
                                  <Dropdown
                                    className="w-full"
                                    source={REGISTRATION_TYPES}
                                    value={item.RegistrationTypeID ?? 1}
                                    onChange={(value) => updateIdpRow(index, { RegistrationTypeID: Number(value) })}
                                    getLabel={(x) => x.Name}
                                    getValue={(x) => x.Id}
                                    searchable={false}
                                    disabled={false}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {selectedUserManagementTypeId !== 2 && (
            <FormField
            
              control={form.control}
              name="AllowIncognito"
              render={({ field }) => (
                <FormItem className="ml-2 mt-2">
                  <FormControl>
                    <Checkbox
                      checked={!!field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                      label="Allow Incognito for explicit login"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
            </div>
          )}
        </div>
      </div>

{(selectedUserManagementTypeId === 2 || selectedUserManagementTypeId === 3 || hideExplicitLogin === false) && (
         <div className="rounded-md border !mt-10">
        <div className="border-b bg-slate-100 px-3 py-2 text-sm font-bold text-gray-600 flex justify-between">
          <div className="flex gap-3">Users<Plus className="cursor-pointer" onClick={() => setSelectedUser({Id:""} as UserInfoSchema)}/></div>
           <SearchForm title="Search User..." onSearch={handleSearchSubmit} />
                   
          
        <div><Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); generateSystemUsers(); }}>
                        Regenerate System Users
                      </Button>
                      <Button className="ml-2" type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setShowImport(true); }}>
                        Import Users
                      </Button>
                      </div></div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium text-gray-600">Username</th>
                <th className="px-3 py-2 font-medium text-gray-600">First Name</th>
                <th className="px-3 py-2 font-medium text-gray-600">Last Name</th>
                <th className="px-3 py-2 font-medium text-gray-600">Allow TalkRequest</th>
                {/* <th className="px-3 py-2 font-medium text-gray-600">RoleIdentifier</th> */}
                <th className="px-3 py-2 font-medium text-gray-600">Presenter</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredData?.length === 0 ? (
                <tr className="border-t">
                  <td colSpan={6} className="px-3 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredData?.map((item) => (
                  <tr key={item.Id} className="border-t align-middle cursor-pointer hover:bg-slate-50" onClick={() => setSelectedUser(item)}>
                    <td className="px-3 py-2">{item.UserName}</td>
                    <td className="px-3 py-2">{item.FirstName}</td>
                    <td className="px-3 py-2">{item.LastName}</td>
                    <td className="px-3 py-2">{item.AllowTalkrequest ? "Yes" : "No"}</td>
                    {/* <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span>{item.RoleIdentifier || "-"}</span>
                        <Button type="button" variant="outline" size="icon" title="Edit role identifier" aria-label="Edit role identifier" onClick={(e) => e.stopPropagation()}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td> */}
                    <td className="px-3 py-2">
                      {item.Presenter!=""&&(
                      <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setPresenter(item.Id); }}>
                        {item.Presenter}
                      </Button>)}
                    </td>
                    <td className="px-3 py-2">
                      {item.UserName.startsWith("event_") ? null : (
                      <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); deleteUser(item.Id); }}>
                        <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
                      </Button>)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>)}
      <Popup
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        title="User"
        className="w-[95vw] max-w-3xl"
      >
        {selectedUser && (
          <UserInfo
            key={selectedUser.Id}
            user={selectedUser}
            eventId={eventId}
            onClose={async (refresh) => {
              setSelectedUser(null);
              if (!refresh) {
                return;
              }

              try {
                await loadUsers();
              } catch (err) {
                console.error(err);
                onError("Failed to load user management settings");
              }
            }}
          />
        )}
      </Popup>


       <Popup
        isOpen={editPresenterId !== null}
        onClose={() => setPresenter(null)}
        title="Presenter"
        className="w-[95vw] max-w-8xl max-h-[90vh] overflow-y-auto"
      >
        {editPresenterId && (
          <PresenterInfo
            userId={editPresenterId}
            eventId={eventId}
            onClose={async (presenter) => {
             
              users.find(u => u.Id === editPresenterId)!.Presenter = ( (presenter?.Plenums?.length?? 0) > 0)?"Edit Presenter":"Set up as Presenter";
              setPresenter(null);

             
            }}
          />
        )}
      </Popup>

       <Popup
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        title="Import Users"
        className="w-[95vw] max-w-8xl max-h-[90vh] overflow-y-auto"
      >
       <Button onClick={downloadSample}>Download Sample File</Button>
       <Button className="ml-2" onClick={() => fileInputRef.current?.click()}>Select File</Button>
         {selectedImportFile && <div className="mt-5">
          <div className="border-b ">File to import:</div>
          <span>{selectedImportFile.name}</span>
        <br/>
        <Checkbox
         className="mt-2" 
            checked={!!autoGeneratePW}
            onCheckedChange={(checked) => setAutoGeneratePW(checked)}
            label="Autogenerate Passwords"
          />
          <br/>
       <Button className="mt-2" onClick={importUsers}  disabled={isUploadingFile}>Import</Button>
        
        </div>}
      </Popup>

       <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
    </div>
  );
}
